import React, {useRef} from 'react';
import {View} from 'react-native';
import {useRoute} from '@react-navigation/native';
import {isFunction, noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {MetricsSection} from '/constant/TestIDs';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {getTextFromReactNode} from '/util/accessibility.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {styles} from './MetricSection.style';
import {generateOnInfoPress} from './MetricSection.util';
import MetricSectionItem from './MetricSectionItem';

export const MetricSection = ({
  analyticsTitle = '',
  infoAccessibilityLabel = '',
  title = '',
  listItems = [],
  onInfoPress = null,
  onItemPress = noop,
  testID = null,
  lastPaidRank,
  containerStyle = undefined,
  headerStyle = undefined,
}: any) => {
  const infoButtonRef = useRef(null);

  const {name} = useRoute();
  const getTitleString = (title: React.ReactNode): string => {
    if (typeof title === 'string') return title;
    if (typeof title === 'number') return String(title);
    return ''; // fallback if it's JSX or something else
  };
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.informationClick,
  });

  // Modified handler that uses the popover provider properly
  const handleInfoPress = generateOnInfoPress({
    analyticsTitle,
    screenName: name,
    onInfoPress: (payload?: any) => {
      const popoverPayload = {
        triggerRef: infoButtonRef,
        ...payload,
      };
      onInfoPress?.(popoverPayload);
    },
    sendEvent,
    lastPaidRank,
  });

  return (
    <View {...testProperties(testID)} style={[styles.base, containerStyle]}>
      <View style={[styles.header, headerStyle]} accessible={false}>
        <KeyboardFocusable
          accessible={true}
          focusable={true}
          accessibilityRole="header"
          accessibilityLabel={`${getTextFromReactNode(title)}`}
          onPress={() => {}}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={false}
            testID={MetricsSection.metricSectionTitle}
            variant="heading"
            style={styles.headerText}>
            {title}
          </Text>
        </KeyboardFocusable>
        {isFunction(onInfoPress) && (
          <KeyboardFocusable
            viewRef={infoButtonRef}
            accessible={true}
            focusable={true}
            accessibilityRole="button"
            accessibilityLabel={infoAccessibilityLabel || 'Information'}
            style={styles.PixelMargin}
            onPress={handleInfoPress}
            focusRingStyle={styles.infoButtonFocusRing}>
            <Icon accessible={false} name="info" style={styles.infoIcon} />
          </KeyboardFocusable>
        )}
      </View>
      <Gutter>
        <View
          accessibilityRole="list"
          accessibilityLabel={`${getTitleString(title)}`}>
          {listItems.map((listItem: any, index: any) => {
            return (
              <MetricSectionItem
                key={`${listItem.recordsName || 'metric'}-${index}`}
                index={index}
                totalItemCount={listItems.length}
                listItem={listItem}
                onItemPress={onItemPress}
              />
            );
          })}
        </View>
      </Gutter>
    </View>
  );
};

export default MetricSection;
